/**
 * reducers/models.ts
 * desc: ai models
 * note: connects to third-party ML systems via Y Meadows API endpoints
 */

import type { Reducer } from '@reduxjs/toolkit';
import { sortZa } from '../../utilities';
import constants from '../../settings/constants';
import type { AccuracyResults, ModelsAction } from '../actions/models';
import type { ModelDto, ModelIntentPerformanceDto, ModelPage, ModelPerformanceDto, TestModelResult, TrainingSetInfoDto } from '../../types/api/models';

export interface ModelAccuracyPoint {
    precision: number;
    recall: number;
}

// accuracy points grouped by intent id, then by threshold
export type ModelAccuracy = Record<string, Record<number, ModelAccuracyPoint>>;

// per intent, which model (1 or 2) wins on precision / recall and by how much
export interface ModelComparison {
    intentId: string;
    intentName: string | null | undefined;
    precision: { model: 1 | 2; value: number };
    recall: { model: 1 | 2; value: number };
}

export interface ModelsDetail {
    accuracy: ModelAccuracy;
    // empty until a model arrives; the test endpoint's result is written onto it as `test`
    model: Partial<ModelDto> & { test?: TestModelResult };
    performance: ModelPerformanceDto[];
    // never written — the test result lands on model.test
    test: Record<string, never>;
    trainingSet: TrainingSetInfoDto[];
}

// empty until the first page arrives
export type ModelsList = Partial<ModelPage>;

export interface ModelsState {
    isLoading: {
        createModel: boolean;
        createTest: boolean;
        deleteModel: boolean;
        getAccuracy: boolean;
        getModel: boolean;
        getModels: boolean;
        getPerformance: boolean;
        getTrainingSet: boolean;
        updateModel: boolean;
    };
    compare: ModelComparison[];
    detail: ModelsDetail;
    list: ModelsList;
}

const INITIAL_STATE: ModelsState = {
    isLoading: {
        createModel: false,
        createTest: false,
        deleteModel: false,
        getAccuracy: false,
        getModel: false,
        getModels: false,
        getPerformance: false,
        getTrainingSet: false,
        updateModel: false,
    },
    compare: [],
    detail: {
        accuracy: {},
        model: {},
        performance: [],
        test: {},
        trainingSet: [],
    },
    list: {},
};


function modelsReducer(state: ModelsState = INITIAL_STATE, action: ModelsAction): ModelsState {
    switch (action.type) {
        // CLEAR Models
        case 'CLEAR_MODELS':
            return _clearModels();

        // CREATE Model (Instance)
        case 'CREATE_MODEL_ERROR':
            return _createModelError(state);
        case 'CREATE_MODEL_REQUEST':
            return _createModelRequest(state);
        case 'CREATE_MODEL_SUCCESS':
            return _createModelSuccess(state, action.payload);

        // CREATE Test Model (Instance)
        case 'CREATE_TEST_MODEL_ERROR':
            return _createTestModelError(state);
        case 'CREATE_TEST_MODEL_REQUEST':
            return _createTestModelRequest(state);
        case 'CREATE_TEST_MODEL_SUCCESS':
            return _createTestModelSuccess(state, action.payload);

        // DELETE Model (Instance)
        case 'DELETE_MODEL_ERROR':
            return _deleteModelError(state);
        case 'DELETE_MODEL_REQUEST':
            return _deleteModelRequest(state);
        case 'DELETE_MODEL_SUCCESS':
            return _deleteModelSuccess(state, action.payload);

        // GET  Accuracy (Instance)
        case 'GET_ACCURACY_ERROR':
            return _getAccuracyError(state);
        case 'GET_ACCURACY_REQUEST':
            return _getAccuracyRequest(state);
        case 'GET_ACCURACY_SUCCESS':
            return _getAccuracySuccess(state, action.payload);

        // GET Model (Instance)
        case 'GET_MODEL_ERROR':
            return _getModelError(state);
        case 'GET_MODEL_REQUEST':
            return _getModelRequest(state);
        case 'GET_MODEL_SUCCESS':
            return _getModelSuccess(state, action.payload);

        // GET Model Performance (Instance)
        case 'GET_MODEL_PERFORMANCE_ERROR':
            return _getModelPerformanceError(state);
        case 'GET_MODEL_PERFORMANCE_REQUEST':
            return _getModelPerformanceRequest(state);
        case 'GET_MODEL_PERFORMANCE_SUCCESS':
            return _getModelPerformanceSuccess(state, action.payload);

        // GET Models (Collection)
        case 'GET_MODELS_ERROR':
            return _getModelsError(state);
        case 'GET_MODELS_REQUEST':
            return _getModelsRequest(state);
        case 'GET_MODELS_SUCCESS':
            return _getModelsSuccess(state, action.payload);

        // GET Training Set (Instance)
        case 'GET_TRAINING_SET_ERROR':
            return _getTrainingSetError(state);
        case 'GET_TRAINING_SET_REQUEST':
            return _getTrainingSetRequest(state);
        case 'GET_TRAINING_SET_SUCCESS':
            return _getTrainingSetSuccess(state, action.payload);

        // Update Model (Instance)
        case 'UPDATE_MODEL_ERROR':
            return _updateModelError(state);
        case 'UPDATE_MODEL_REQUEST':
            return _updateModelRequest(state);
        case 'UPDATE_MODEL_SUCCESS':
            return _updateModelSuccess(state, action.payload);

        default:
            return state;
    }
}



//*************************************************
//*****   CLEAR Models   **************************
//*************************************************
function _clearModels(): ModelsState {
    return INITIAL_STATE;
}



//*************************************************
//*****   CREATE Model (Instance)   ***************
//*************************************************
function _createModelError(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createModel: false,
        },
    };
}

function _createModelRequest(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createModel: true,
        },
    };
}

function _createModelSuccess(state: ModelsState, payload: ModelDto): ModelsState {
    let modelsList: ModelDto[] = [];

    // if list exists, modify w new model
    // note: list will not exist if deeplinked or refreshed create page
    if (state.list.models?.length) {
        modelsList = [ ...state.list.models, payload ];
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createModel: false,
        },
        detail: {
            ...state.detail,
            model: payload,
        },
        list: {
            ...state.list,
            models: modelsList,
        },
    };
}



//*************************************************
//*****   CREATE Test Model (Instance)   **********
//*************************************************
function _createTestModelError(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createTest: false,
        },
    };
}

function _createTestModelRequest(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createTest: true,
        },
    };
}

function _createTestModelSuccess(state: ModelsState, payload: TestModelResult): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createTest: false,
        },
        detail: {
            ...state.detail,
            model: {
                ...state.detail.model,
                test: payload
            }
        }
    };
}



//*************************************************
//*****   DELETE Model (Instance)   ***************
//*************************************************
function _deleteModelError(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteModel: false,
        },
    };
}

function _deleteModelRequest(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteModel: true,
        },
    };
}

function _deleteModelSuccess(state: ModelsState, modelId: string): ModelsState {
    // create new list without deleted model
    // note: the list is absent on a deep link; deleting from there throws, as it always has
    const models = state.list.models as ModelDto[];
    const [ ...modelsList ] = models;
    const deletedIndex = models.findIndex(x => x.id === modelId);
    if (deletedIndex > -1) modelsList.splice(deletedIndex, 1);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteModel: false,
        },
        list: {
            ...state.list,
            models: modelsList,
        },
    };
}



//*************************************************
//*****   GET Accuracy (Instance)   ***************
//*************************************************
function _getAccuracyError(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getAccuracy: false,
        },
    };
}

function _getAccuracyRequest(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getAccuracy: true,
        },
    };
}

function _getAccuracySuccess(state: ModelsState, payload: AccuracyResults): ModelsState {
    const { model1, model2 } = payload;
    const compareData = model2 ? _getCompareData(model1, model2) : [];

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getAccuracy: false,
        },
        compare: compareData,
        detail: {
            ...state.detail,
            accuracy: _getAccuracyData(model1),
        },
    };
}



//*************************************************
//*****   GET Model (Instance)   ******************
//*************************************************
function _getModelError(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getModel: false,
        },
    };
}

function _getModelRequest(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getModel: true,
        },
    };
}

function _getModelSuccess(state: ModelsState, payload: ModelDto): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getModel: false,
        },
        detail: {
            ...state.detail,
            model: payload
        }
    };
}



//*************************************************
//*****   GET Model Performance (Instance)   ******
//*************************************************
function _getModelPerformanceError(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPerformance: false,
        },
    };
}

function _getModelPerformanceRequest(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPerformance: true,
        },
    };
}

function _getModelPerformanceSuccess(state: ModelsState, payload: ModelPerformanceDto[]): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPerformance: false,
        },
        detail: {
            ...state.detail,
            performance: sortZa(payload, 'precision')
        }
    };
}



//*************************************************
//*****   GET Models (Collection)   ***************
//*************************************************
function _getModelsError(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getModels: false,
        },
    };
}

function _getModelsRequest(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getModels: true,
        },
    };
}

function _getModelsSuccess(state: ModelsState, payload: ModelPage): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getModels: false,
        },
        list: payload,
    };
}



//*************************************************
//*****   GET Training Set (Instance)   ***********
//*************************************************
function _getTrainingSetError(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTrainingSet: false,
        },
    };
}

function _getTrainingSetRequest(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTrainingSet: true,
        },
    };
}

function _getTrainingSetSuccess(state: ModelsState, payload: TrainingSetInfoDto[]): ModelsState {
    // sort by largest count w "other" as the last item
    const otherIntent = payload.find(x => x.intentId === constants.INTENTS_OTHER_ID);
    const customIntents = payload.filter(x => x.intentId !== constants.INTENTS_OTHER_ID);
    // @ts-expect-error — when the backend omits the Other intent the last entry is undefined; the hole is kept as it always was
    const allIntents: TrainingSetInfoDto[] = [ ...sortZa(customIntents, 'count'), otherIntent ];

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTrainingSet: false,
        },
        detail: {
            ...state.detail,
            trainingSet: allIntents,
        },
    };
}



//*************************************************
//*****   UPDATE Model (Instance)   ***************
//*************************************************
function _updateModelError(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateModel: false,
        },
    };
}

function _updateModelRequest(state: ModelsState): ModelsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateModel: true,
        },
    };
}

function _updateModelSuccess(state: ModelsState, payload: ModelDto): ModelsState {
    let modelsList: ModelDto[] = [];

    // if list exists, modify w updated model
    // note: list will not exist if deeplinked or refreshed detail page
    if (state.list?.models) {
        modelsList = [ ...state.list.models ];
        if (modelsList.length) {
            const updatedIndex = modelsList.findIndex(x => x.id === payload.id);
            if (updatedIndex > -1) modelsList[updatedIndex] = payload;
        }
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateModel: false,
        },
        detail: {
            ...state.detail,
            model: payload,
        },
        list: {
            ...state.list,
            models: modelsList,
        }
    };
}



//*************************************************
//*****   Helper Functions   **********************
//*************************************************

/**
 * _getAccuracyData
 * desc: group accuracy data by intent
 * note: used by getAccuracy action/reducer
 * @param  {array}  m1  Model one
 * @return {array}      List of intents w results
 */
function _getAccuracyData(m1: ModelIntentPerformanceDto[]): ModelAccuracy {
    const result: ModelAccuracy = {};
    m1.forEach(item => {
        if (result[item.intentId]) {
            result[item.intentId][item.threshold] = {
                precision: item.precision,
                recall: item.recall
            };
        } else {
            result[item.intentId] = {
                [item.threshold]: {
                    precision: item.precision,
                    recall: item.recall
                }
            };
        }
    });
    return result;
}



/**
 * _getCompareData
 * desc: precision/accuracy comparision for two models
 * note: used by getAccuracy action/reducer
 * @param  {array}  m1  Model one
 * @param  {array}  m2  Model two
 * @return {array}      List of intents w results
 */
function _getCompareData(m1: ModelIntentPerformanceDto[], m2: ModelIntentPerformanceDto[]): ModelComparison[] {
    const allIntentIds = _getUniqListOfValuesByKey(m1, m2, 'intentId');

    const compareData: ModelComparison[] = [];

    // iterate over intents
    allIntentIds.forEach(intentId => {
        const defaultData: Pick<ModelIntentPerformanceDto, 'precision' | 'recall' | 'intentName'> = { precision:0, recall:0 };

        // find model intent (if undefined, fallback to defaults)
        const m1Data = m1.find(x => x.intentId === intentId) || defaultData;
        const m2Data = m2.find(x => x.intentId === intentId) || defaultData;

        // perform calculation
        // note: model1 is always first, so we can determine better model
        // e.g.: model1 wins if positive number; model2 wins if negative number
        const precision = m1Data.precision - m2Data.precision;
        const recall = m1Data.recall - m2Data.recall;

        // add intent results to compareData list
        // note: intentName must exist in at least one model
        compareData.push({
            intentId,
            intentName: m1Data.intentName || m2Data.intentName,
            precision: {
                model: (precision > 0) ? 1 : 2,
                value: Math.abs(precision),
            },
            recall: {
                model: (recall > 0) ? 1 : 2,
                value: Math.abs(recall),
            },
        });
    });

    return compareData;
}

/**
 * _getUniqListOfValuesByKey
 * desc: returns a combined list of unique values
 * note: used for getting a list of unique intentIds from two models
 * @param  {array}  arr1  List one
 * @param  {array}  arr2  List two
 * @param  {string} key   Object property to combine
 * @return {array}        Combined list of properties
 */
function _getUniqListOfValuesByKey<T, K extends keyof T>(arr1: T[], arr2: T[], key: K): T[K][] {
    const arr1Keys = arr1.map(x => x[key]);
    const arr2Keys = arr2.map(x => x[key]);
    return [ ...new Set([ ...arr1Keys, ...arr2Keys ]) ];
}


export default modelsReducer as Reducer<ModelsState>;
